import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Sync as SyncIcon,
  CloudSync as CloudSyncIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { IntegrationService } from '../../services/api/integration';
import { formatDistanceToNow } from 'date-fns';

interface EmailSyncStatusProps {
  integrationId: string;
  provider: string;
  onSyncTrigger?: () => void;
}

interface SyncHistory {
  id: string;
  sync_started_at: string;
  sync_completed_at?: string;
  emails_processed: number;
  emails_added_to_kg: number;
  oldest_email_date?: string;
  newest_email_date?: string;
  status: 'syncing' | 'completed' | 'failed';
  error_message?: string;
}

interface SyncStatusData {
  integrationId: string;
  currentStatus: 'idle' | 'syncing' | 'completed' | 'failed';
  lastSyncAt?: string;
  syncHistory: SyncHistory[];
}

const EmailSyncStatus: React.FC<EmailSyncStatusProps> = ({
  integrationId,
  provider,
  onSyncTrigger
}) => {
  const [statusData, setStatusData] = useState<SyncStatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const integrationService = useMemo(() => new IntegrationService(), []);

  const fetchSyncStatus = useCallback(async () => {
    try {
      setError(null);
      const response = await integrationService.getEmailSyncStatus(integrationId);
      setStatusData(response);
    } catch (err) {
      console.error('Failed to fetch sync status:', err);
      setError('Failed to load sync status');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [integrationId, integrationService]);

  useEffect(() => {
    fetchSyncStatus();

    // Poll for updates every 5 seconds if currently syncing
    const interval = setInterval(() => {
      if (statusData?.currentStatus === 'syncing') {
        fetchSyncStatus();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [integrationId, statusData?.currentStatus, fetchSyncStatus]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchSyncStatus();
  };

  const handleTriggerSync = async () => {
    try {
      setError(null);
      await integrationService.syncEmailsToKnowledgeGraph(integrationId);
      if (onSyncTrigger) {
        onSyncTrigger();
      }
      // Start polling for updates
      setTimeout(fetchSyncStatus, 1000);
    } catch (err) {
      console.error('Failed to trigger sync:', err);
      setError('Failed to start email sync');
    }
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
    switch (status) {
      case 'syncing':
        return 'primary';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'syncing':
        return <CircularProgress size={16} />;
      case 'completed':
        return <CheckCircleIcon fontSize="small" />;
      case 'failed':
        return <ErrorIcon fontSize="small" />;
      default:
        return <InfoIcon fontSize="small" />;
    }
  };

  const getCurrentSync = (): SyncHistory | undefined => {
    if (statusData?.currentStatus === 'syncing' && statusData.syncHistory.length > 0) {
      return statusData.syncHistory.find(h => h.status === 'syncing');
    }
    return undefined;
  };

  const getLastCompletedSync = (): SyncHistory | undefined => {
    if (statusData?.syncHistory) {
      return statusData.syncHistory.find(h => h.status === 'completed');
    }
    return undefined;
  };

  if (loading && !refreshing) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 16 }}>
        <CircularProgress size={24} />
      </div>
    );
  }

  if (error && !statusData) {
    return (
      <Alert severity="error" style={{ marginBottom: 16 }}>
        {error}
      </Alert>
    );
  }

  const currentSync = getCurrentSync();
  const lastCompletedSync = getLastCompletedSync();
  const isSyncing = statusData?.currentStatus === 'syncing';

  // Calculate progress percentage for current sync
  const getProgressValue = () => {
    if (!currentSync || currentSync.emails_processed === 0) return 0;
    // Estimate based on typical sync (this could be improved with more data)
    return Math.min((currentSync.emails_processed / 500) * 100, 95);
  };

  return (
    <Card style={{ marginBottom: 16 }}>
      <CardContent>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <EmailIcon color="primary" />
            <Typography variant="subtitle1" fontWeight="medium">
              Email Sync Status
            </Typography>
            {statusData && (
              <Chip
                size="small"
                label={statusData.currentStatus}
                color={getStatusColor(statusData.currentStatus)}
                icon={getStatusIcon(statusData.currentStatus)}
              />
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tooltip title="Refresh status">
              <IconButton
                size="small"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title={expanded ? "Hide details" : "Show details"}>
              <IconButton
                size="small"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
          </div>
        </div>

        {/* Current Sync Progress */}
        {isSyncing && currentSync && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <Typography variant="body2" color="text.secondary">
                Syncing emails...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentSync.emails_processed} emails processed
              </Typography>
            </div>
            <LinearProgress
              variant="determinate"
              value={getProgressValue()}
              style={{ height: 8, borderRadius: 4 }}
            />
            {currentSync.emails_added_to_kg > 0 && (
              <Typography variant="caption" color="text.secondary" style={{ display: 'block', marginTop: 8 }}>
                {currentSync.emails_added_to_kg} emails added to knowledge graph
              </Typography>
            )}
          </div>
        )}

        {/* Last Sync Summary */}
        {lastCompletedSync && !isSyncing && (
          <div style={{ marginBottom: 16 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Last sync completed {formatDistanceToNow(new Date(lastCompletedSync.sync_completed_at!))} ago
            </Typography>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                icon={<EmailIcon />}
                label={`${lastCompletedSync.emails_processed} emails`}
                variant="outlined"
              />
              <Chip
                size="small"
                icon={<CloudSyncIcon />}
                label={`${lastCompletedSync.emails_added_to_kg} synced`}
                variant="outlined"
                color="primary"
              />
              {lastCompletedSync.newest_email_date && (
                <Chip
                  size="small"
                  icon={<ScheduleIcon />}
                  label={`Latest: ${new Date(lastCompletedSync.newest_email_date).toLocaleDateString()}`}
                  variant="outlined"
                />
              )}
            </div>
          </div>
        )}

        {/* Sync Button */}
        {!isSyncing && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<SyncIcon />}
            onClick={handleTriggerSync}
            fullWidth
          >
            Start Email Sync
          </Button>
        )}

        {/* Expanded Details */}
        <Collapse in={expanded}>
          <div style={{ marginTop: 16 }}>
            <Typography variant="subtitle2" gutterBottom>
              Sync History
            </Typography>
            {statusData?.syncHistory && statusData.syncHistory.length > 0 ? (
              <List dense>
                {statusData.syncHistory.slice(0, 5).map((history) => (
                  <ListItem key={history.id}>
                    <ListItemIcon>
                      {getStatusIcon(history.status)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Typography variant="body2">
                            {new Date(history.sync_started_at).toLocaleString()}
                          </Typography>
                          <Chip
                            size="small"
                            label={history.status}
                            color={getStatusColor(history.status)}
                          />
                        </div>
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                      secondary={
                        <>
                          {history.emails_processed > 0 && (
                            <Typography variant="caption" component="span" display="block">
                              {history.emails_processed} emails processed, {history.emails_added_to_kg} added to knowledge graph
                            </Typography>
                          )}
                          {history.error_message && (
                            <Typography variant="caption" color="error" component="span" display="block">
                              Error: {history.error_message}
                            </Typography>
                          )}
                          {history.sync_completed_at && history.status === 'completed' && (
                            <Typography variant="caption" color="text.secondary" component="span" display="block">
                              Duration: {formatDistanceToNow(new Date(history.sync_started_at), {
                                addSuffix: false,
                                includeSeconds: true
                              })}
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No sync history available
              </Typography>
            )}
          </div>
        </Collapse>

        {error && (
          <Alert severity="error" style={{ marginTop: 16 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default EmailSyncStatus;
